import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, CheckCircle2 } from "lucide-react";
import { SERVICES } from "@/lib/lumiqoData";

const Services = () => {
  const [expanded, setExpanded] = useState(null);

  return (
    <section
      id="services"
      className="relative py-24 sm:py-32 bg-white"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-2xl">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="inline-block px-3 py-1 rounded-full bg-[#1d72ff]/10 text-[#1d72ff] text-xs font-semibold tracking-wide uppercase"
          >
            What We Do
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.08 }}
            className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl text-[#0a1d4d] mt-5 leading-[1.05]"
            data-testid="services-heading"
          >
            A creative studio,
            <br />
            built to scale your brand.
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.16 }}
            className="mt-5 text-base sm:text-lg text-[#0a1d4d]/65 max-w-xl"
          >
            Every service is engineered for attention, retention and reach —
            so your content stops being a cost center and starts being a
            growth engine.
          </motion.p>
        </div>

        <div className="mt-14 grid sm:grid-cols-2 gap-5 sm:gap-6">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            const isOpen = expanded === i;
            const toggle = () => setExpanded(isOpen ? null : i);
            return (
              <motion.article
                key={s.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: i * 0.06 }}
                onClick={toggle}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggle();
                  }
                }}
                role="button"
                tabIndex={0}
                aria-expanded={isOpen}
                className={`group relative p-7 rounded-3xl bg-white border lq-shadow-soft cursor-pointer transition-all duration-300 ${
                  isOpen
                    ? "border-[#1d72ff]/40 lq-shadow-glow"
                    : "border-[#0a1d4d]/10 hover:-translate-y-1 hover:lq-shadow-glow"
                }`}
                data-testid={`service-card-${i}`}
              >
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-[#1d72ff]/0 via-transparent to-[#1d72ff]/0 group-hover:from-[#1d72ff]/5 group-hover:to-[#0a1d4d]/5 transition-all duration-500 pointer-events-none" />
                <div className="relative">
                  <div className="relative w-14 h-14 rounded-2xl bg-gradient-to-br from-[#f5f6fa] to-white border border-[#0a1d4d]/8 flex items-center justify-center text-[#0a1d4d] group-hover:border-[#1d72ff]/30 transition-all duration-300">
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[#1d72ff]/0 to-[#1d72ff]/0 group-hover:from-[#1d72ff]/8 group-hover:to-[#1d72ff]/0 transition-all duration-300" />
                    <Icon
                      className="relative w-6 h-6 group-hover:text-[#1d72ff] transition-colors duration-300"
                      strokeWidth={1.4}
                    />
                  </div>
                  <h3 className="font-display font-bold text-xl text-[#0a1d4d] mt-5 tracking-tight">
                    {s.title}
                  </h3>
                  <p className="text-[#0a1d4d]/65 text-sm mt-2 leading-relaxed">
                    {s.desc}
                  </p>
                  <div className="mt-6 flex items-center justify-between">
                    <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1d72ff]">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#1d72ff] lq-pulse" />
                      {s.tag}
                    </div>
                    <div
                      className="inline-flex items-center gap-1 text-xs font-semibold text-[#0a1d4d]/50 group-hover:text-[#1d72ff] transition-colors"
                      data-testid={`service-toggle-${i}`}
                    >
                      {isOpen ? "Hide details" : "View details"}
                      <ChevronDown
                        className={`w-3.5 h-3.5 transition-transform duration-300 ${
                          isOpen ? "rotate-180" : ""
                        }`}
                      />
                    </div>
                  </div>

                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: "easeInOut" }}
                        className="overflow-hidden"
                        data-testid={`service-details-${i}`}
                      >
                        <div className="pt-5 mt-5 border-t border-[#0a1d4d]/10 space-y-3.5">
                          {s.details.map((d) => (
                            <div key={d.name} className="flex items-start gap-3">
                              <CheckCircle2
                                className="w-4 h-4 mt-0.5 shrink-0 text-[#1d72ff]"
                                strokeWidth={2}
                              />
                              <div>
                                <div className="text-sm font-semibold text-[#0a1d4d]">
                                  {d.name}
                                </div>
                                <div className="text-xs text-[#0a1d4d]/60 mt-0.5 leading-relaxed">
                                  {d.note}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
